import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api, CATEGORIES } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Plus, X, MapPin, Search, Loader2, Upload, FileText } from 'lucide-react';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { toast } from 'sonner';
import SEO from '@/components/SEO';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

export default function PostTask() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const [form, setForm] = useState({
    title: '', description: '', location_name: '', latitude: null, longitude: null,
    category: '', type: 'volunteer', pay_amount: '', date: '', end_date: '', start_time: '', end_time: '',
    duration: '', spots: 1, skills_needed: [], zip_code: '',
  });
  const [skill, setSkill] = useState('');
  const [loading, setLoading] = useState(false);
  const [attachedFile, setAttachedFile] = useState(null);
  const [attachedFileName, setAttachedFileName] = useState('');
  const [existingFilePath, setExistingFilePath] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);

  // Load existing opportunity for edit mode
  useEffect(() => {
    if (editId) {
      (async () => {
        try {
          const res = await api.get(`/opportunities/${editId}`);
          const opp = res.data;
          setForm({
            title: opp.title || '', description: opp.description || '',
            location_name: opp.location?.name || '', latitude: opp.location?.lat, longitude: opp.location?.lng,
            category: opp.category || '', type: opp.type || 'volunteer',
            pay_amount: opp.pay_amount || '', date: opp.date || '', end_date: opp.end_date || '',
            start_time: opp.start_time || '', end_time: opp.end_time || '',
            duration: opp.duration || '', spots: opp.spots || 1,
            skills_needed: opp.skills_needed || [], zip_code: opp.zip_code || '',
          });
          if (opp.image_path) setExistingFilePath(opp.image_path);
          if (opp.location?.name) setAddressQuery(opp.location.name);
        } catch {
          toast.error('Failed to load task for editing');
          navigate('/explore');
        }
      })();
    }
  }, [editId]);

  // Address search state
  const [addressQuery, setAddressQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const searchAddress = (query) => {
    setAddressQuery(query);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&addressdetails=1&limit=5`,
          { headers: { 'Accept-Language': 'en' } }
        );
        const data = await res.json();
        setSuggestions(data);
        setShowSuggestions(data.length > 0);
      } catch {
        setSuggestions([]);
      }
      setSearching(false);
    }, 500);
  };

  const selectAddress = (place) => {
    const addr = place.address || {};
    const zipCode = addr.postcode || '';
    const fullAddress = place.display_name || '';
    setForm({
      ...form,
      location_name: fullAddress,
      latitude: parseFloat(place.lat),
      longitude: parseFloat(place.lon),
      zip_code: zipCode,
    });
    setAddressQuery(fullAddress);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const addSkill = () => {
    if (skill.trim() && !form.skills_needed.includes(skill.trim())) {
      setForm({ ...form, skills_needed: [...form.skills_needed, skill.trim()] });
      setSkill('');
    }
  };

  const removeSkill = (s) => {
    setForm({ ...form, skills_needed: form.skills_needed.filter(x => x !== s) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.description || !form.category || !form.date || !form.duration || !form.start_time || !form.end_time) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (!form.latitude || !form.longitude) {
      toast.error('Please search and select a location');
      return;
    }
    setLoading(true);
    try {
      let filePath = existingFilePath;
      if (attachedFile) {
        setUploadingFile(true);
        const formData = new FormData();
        formData.append('file', attachedFile);
        const uploadRes = await api.post('/upload/opportunity-file', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        filePath = uploadRes.data.path;
        setUploadingFile(false);
      }
      const payload = {
        ...form,
        pay_amount: form.type === 'paid' ? parseFloat(form.pay_amount) : null,
        duration: parseFloat(form.duration),
        spots: parseInt(form.spots),
        latitude: parseFloat(form.latitude),
        longitude: parseFloat(form.longitude),
        zip_code: form.zip_code,
        start_time: form.start_time,
        end_time: form.end_time,
        end_date: form.end_date || '',
        image_path: filePath,
      };
      if (editId) {
        await api.put(`/opportunities/${editId}`, payload);
        toast.success('Task updated!');
        navigate(`/opportunities/${editId}`);
      } else {
        const res = await api.post('/opportunities', payload);
        toast.success('Task posted!');
        navigate(`/opportunities/${res.data.opp_id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${editId ? 'update' : 'post'} task`);
    }
    setLoading(false);
  };

  return (
    <div className="px-4 pt-6 max-w-lg mx-auto pb-8" data-testid="post-task-page">
      <SEO
        title={editId ? 'Edit Task' : 'Post a Task'}
        description="Create or edit a volunteer opportunity or paid side gig for teens in your community on TeenHands."
        path="/post-task"
      />
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[#5C6B7F] font-bold mb-4" data-testid="back-button">
        <ArrowLeft size={20} /> Back
      </button>

      <h1 className="text-3xl font-bold text-[#011627] mb-6 animate-fade-in-up">{editId ? 'Edit Task' : 'Post a Task'}</h1>

      <form onSubmit={handleSubmit} className="space-y-4 animate-fade-in-up" style={{ animationDelay: '100ms' }}>
        <Input
          placeholder="Task Title *"
          value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
          className="bg-white border-2 border-gray-100 focus:border-[#2EC4B6] rounded-2xl px-4 py-3 font-semibold"
          data-testid="task-title" required
        />

        <Textarea
          placeholder="Describe the task in detail *"
          value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
          className="bg-white border-2 border-gray-100 focus:border-[#2EC4B6] rounded-2xl font-semibold min-h-[120px]"
          data-testid="task-description" required
        />

        <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
          <SelectTrigger className="bg-white border-2 border-gray-100 rounded-2xl py-3 font-semibold" data-testid="task-category">
            <SelectValue placeholder="Category *" />
          </SelectTrigger>
          <SelectContent>
            {CATEGORIES.map(c => (
              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex gap-2">
          {['volunteer', 'paid'].map(t => (
            <button
              key={t} type="button"
              onClick={() => setForm({ ...form, type: t })}
              className={`flex-1 py-3 rounded-2xl font-bold text-sm border-2 transition-all ${
                form.type === t
                  ? t === 'volunteer' ? 'border-[#2EC4B6] bg-[#2EC4B6]/10 text-[#2EC4B6]' : 'border-[#FF9F1C] bg-[#FF9F1C]/10 text-[#FF9F1C]'
                  : 'border-gray-100 text-[#5C6B7F]'
              }`}
              data-testid={`type-${t}`}
            >
              {t === 'volunteer' ? 'Volunteer' : 'Paid'}
            </button>
          ))}
        </div>

        {form.type === 'paid' && (
          <Input
            type="number" placeholder="Pay Amount ($)" min={0} step={0.01}
            value={form.pay_amount} onChange={e => setForm({ ...form, pay_amount: e.target.value })}
            className="bg-white border-2 border-gray-100 focus:border-[#FF9F1C] rounded-2xl px-4 py-3 font-semibold"
            data-testid="task-pay"
          />
        )}

        {/* Address Search */}
        <div className="relative" ref={suggestionsRef}>
          <div className="relative">
            <MapPin size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#2EC4B6]" />
            <Input
              placeholder="Search address or location *"
              value={addressQuery}
              onChange={e => searchAddress(e.target.value)}
              onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
              className="pl-11 pr-10 bg-white border-2 border-gray-100 focus:border-[#2EC4B6] rounded-2xl px-4 py-3 font-semibold"
              data-testid="task-address-search"
            />
            {searching && (
              <Loader2 size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#2EC4B6] animate-spin" />
            )}
          </div>

          {/* Suggestions Dropdown */}
          {showSuggestions && (
            <div className="absolute z-50 w-full mt-1 bg-white rounded-2xl border-2 border-gray-100 shadow-lg overflow-hidden max-h-60 overflow-y-auto" data-testid="address-suggestions">
              {suggestions.map((place, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => selectAddress(place)}
                  className="w-full text-left px-4 py-3 hover:bg-[#2EC4B6]/5 border-b border-gray-50 last:border-0 transition-colors"
                  data-testid={`address-suggestion-${i}`}
                >
                  <p className="text-sm font-semibold text-[#011627] truncate">{place.display_name}</p>
                  <p className="text-xs text-[#5C6B7F]">
                    {place.address?.postcode && `Zip: ${place.address.postcode}`}
                    {place.address?.city && ` · ${place.address.city}`}
                    {place.address?.state && `, ${place.address.state}`}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Selected Address Info */}
        {form.latitude && form.longitude && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              {form.zip_code && (
                <span className="px-3 py-1 bg-[#3A86FF]/10 text-[#3A86FF] rounded-full text-xs font-bold">
                  Zip: {form.zip_code}
                </span>
              )}
              <span className="text-xs text-[#9AA5B1] font-semibold">
                {form.latitude.toFixed(4)}, {form.longitude.toFixed(4)}
              </span>
            </div>

            {/* Map Preview */}
            <div className="rounded-2xl overflow-hidden border-2 border-gray-100" style={{ height: '160px' }} data-testid="location-map-preview">
              <MapContainer
                center={[form.latitude, form.longitude]}
                zoom={14}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={false}
                key={`${form.latitude}-${form.longitude}`}
              >
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <Marker position={[form.latitude, form.longitude]} />
              </MapContainer>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs font-bold text-[#5C6B7F] mb-1 block">Start Date *</label>
            <Input
              type="date" value={form.date}
              onChange={e => setForm({ ...form, date: e.target.value })}
              className="bg-white border-2 border-gray-100 focus:border-[#2EC4B6] rounded-2xl px-4 py-3 font-semibold"
              data-testid="task-date" required
            />
          </div>
          <div>
            <label className="text-xs font-bold text-[#5C6B7F] mb-1 block">End Date</label>
            <Input
              type="date" value={form.end_date}
              onChange={e => setForm({ ...form, end_date: e.target.value })}
              className="bg-white border-2 border-gray-100 focus:border-[#2EC4B6] rounded-2xl px-4 py-3 font-semibold"
              data-testid="task-end-date"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs font-bold text-[#5C6B7F] mb-1 block">Start Time *</label>
            <Input
              type="time" value={form.start_time}
              onChange={e => setForm({ ...form, start_time: e.target.value })}
              className="bg-white border-2 border-gray-100 focus:border-[#2EC4B6] rounded-2xl px-4 py-3 font-semibold"
              data-testid="task-start-time" required
            />
          </div>
          <div>
            <label className="text-xs font-bold text-[#5C6B7F] mb-1 block">End Time *</label>
            <Input
              type="time" value={form.end_time}
              onChange={e => setForm({ ...form, end_time: e.target.value })}
              className="bg-white border-2 border-gray-100 focus:border-[#2EC4B6] rounded-2xl px-4 py-3 font-semibold"
              data-testid="task-end-time" required
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Input
            type="number" placeholder="Duration (hours) *" min={0.5} step={0.5}
            value={form.duration} onChange={e => setForm({ ...form, duration: e.target.value })}
            className="bg-white border-2 border-gray-100 rounded-2xl px-4 py-3 font-semibold"
            data-testid="task-duration" required
          />
          <Input
            type="number" placeholder="Spots *" min={1}
            value={form.spots} onChange={e => setForm({ ...form, spots: e.target.value })}
            className="bg-white border-2 border-gray-100 rounded-2xl px-4 py-3 font-semibold"
            data-testid="task-spots" required
          />
        </div>

        {/* Skills */}
        <div>
          <div className="flex gap-2">
            <Input
              placeholder="Add a skill needed"
              value={skill} onChange={e => setSkill(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addSkill())}
              className="bg-white border-2 border-gray-100 rounded-2xl px-4 py-3 font-semibold"
              data-testid="task-skill-input"
            />
            <Button type="button" onClick={addSkill} className="rounded-2xl bg-[#6C63FF] hover:bg-[#5B52FF] text-white px-4" data-testid="add-skill-btn">
              <Plus size={18} />
            </Button>
          </div>
          {form.skills_needed.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {form.skills_needed.map(s => (
                <span key={s} className="px-3 py-1 bg-[#6C63FF]/10 text-[#6C63FF] rounded-full text-sm font-bold flex items-center gap-1">
                  {s}
                  <button type="button" onClick={() => removeSkill(s)}><X size={14} /></button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* File Attachment (optional) */}
        <div>
          <p className="text-sm font-bold text-[#011627] mb-2">Attachment (optional)</p>
          {(attachedFile || existingFilePath) ? (
            <div className="flex items-center gap-3 bg-gray-50 rounded-2xl p-4 border-2 border-gray-100">
              <div className="w-10 h-10 bg-[#2EC4B6]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <FileText size={20} className="text-[#2EC4B6]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-[#011627] truncate">
                  {attachedFileName || existingFilePath.split('/').pop()}
                </p>
                <p className="text-xs text-[#9AA5B1]">
                  {attachedFile ? `${(attachedFile.size / 1024 / 1024).toFixed(1)} MB` : 'Previously uploaded'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => { setAttachedFile(null); setAttachedFileName(''); setExistingFilePath(''); }}
                className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-gray-500 hover:bg-gray-300 flex-shrink-0"
                data-testid="remove-attachment"
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center h-28 bg-gray-50 border-2 border-dashed border-gray-200 rounded-2xl cursor-pointer hover:border-[#2EC4B6] transition-colors" data-testid="upload-attachment">
              <Upload size={24} className="text-[#9AA5B1] mb-2" />
              <span className="text-sm font-semibold text-[#9AA5B1]">Upload an image or PDF</span>
              <span className="text-xs text-[#9AA5B1]">Any size</span>
              <input
                type="file" accept="image/*,.pdf" className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const allowed = ['image/jpeg','image/png','image/webp','image/gif','application/pdf'];
                    if (!allowed.includes(file.type)) { toast.error('Only images and PDFs are allowed'); return; }
                    setAttachedFile(file);
                    setAttachedFileName(file.name);
                  }
                }}
              />
            </label>
          )}
        </div>

        <Button
          type="submit" disabled={loading}
          className="w-full rounded-full py-6 bg-[#FF9F1C] hover:bg-[#E58E19] text-white font-bold text-lg btn-bounce"
          data-testid="submit-task"
        >
          {loading ? (uploadingFile ? 'Uploading file...' : (editId ? 'Saving...' : 'Posting...')) : (editId ? 'Save Changes' : 'Post Task')}
        </Button>
      </form>
    </div>
  );
}
